import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Accordion, Card } from 'react-bootstrap';
import docData from './docData';
import './Documentation.css';

const Documentation = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('getting-started');
    const [activeSection, setActiveSection] = useState('introduction');
    const [filteredCategories, setFilteredCategories] = useState(docData.categories);

    // Filter categories and sections based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredCategories(docData.categories);
            return;
        }

        const term = searchTerm.toLowerCase();
        const filtered = docData.categories.map(category => {
            const matchingSections = category.sections.filter(section =>
                section.title.toLowerCase().includes(term) ||
                section.content.toLowerCase().includes(term)
            );
            return { ...category, sections: matchingSections };
        }).filter(category => category.sections.length > 0);

        setFilteredCategories(filtered);
    }, [searchTerm]);

    // Scroll to section when clicking sidebar
    const handleSectionClick = (categoryId, sectionId) => {
        setActiveCategory(categoryId);
        setActiveSection(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Highlight search term in content
    const highlightText = (text, term) => {
        if (!term.trim()) return text;
        const regex = new RegExp(`(${term})`, 'gi');
        return text.split(regex).map((part, i) =>
            regex.test(part) ? <mark key={i} className="doc-highlight">{part}</mark> : part
        );
    };

    return (
        <Container fluid className="documentation-page">
            <Row>
                {/* Sidebar */}
                <Col md={3} className="doc-sidebar">
                    <div className="doc-sidebar-header">
                        <h4>Documentation</h4>
                    </div>

                    {/* Search */}
                    <div className="doc-search">
                        <Form.Control
                            type="text"
                            placeholder="Search documentation..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="doc-search-input"
                        />
                    </div>

                    {/* Navigation */}
                    <nav className="doc-nav">
                        {filteredCategories.map(category => (
                            <div key={category.id} className="doc-nav-category">
                                <div
                                    className={`doc-nav-category-title ${activeCategory === category.id ? 'active' : ''}`}
                                    onClick={() => setActiveCategory(category.id)}
                                >
                                    <span className="doc-nav-icon">{category.icon}</span>
                                    {category.title}
                                </div>
                                {activeCategory === category.id && (
                                    <ul className="doc-nav-sections">
                                        {category.sections.map(section => (
                                            <li
                                                key={section.id}
                                                className={`doc-nav-section ${activeSection === section.id ? 'active' : ''}`}
                                                onClick={() => handleSectionClick(category.id, section.id)}
                                            >
                                                {section.title}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </nav>
                </Col>

                {/* Main Content */}
                <Col md={9} className="doc-content">
                    {filteredCategories.length === 0 ? (
                        <div className="doc-no-results">
                            <h3>No results found</h3>
                            <p>Try a different search term or browse the categories on the left.</p>
                        </div>
                    ) : (
                        filteredCategories.map(category => (
                            <div key={category.id} className="doc-category-content">
                                <h2 className="doc-category-title">
                                    <span className="doc-category-icon">{category.icon}</span>
                                    {category.title}
                                </h2>

                                {category.sections.map(section => (
                                    <div key={section.id} id={section.id} className="doc-section">
                                        <h3 className="doc-section-title">{section.title}</h3>
                                        <div className="doc-section-content">
                                            {highlightText(section.content, searchTerm).toString().split('\n').map((line, i) => {
                                                // Handle headers
                                                if (line.startsWith('**') && line.endsWith('**')) {
                                                    return <strong key={i}>{line.replace(/\*\*/g, '')}</strong>;
                                                }
                                                // Handle list items
                                                if (line.startsWith('- ')) {
                                                    return <li key={i}>{line.substring(2)}</li>;
                                                }
                                                // Handle numbered items
                                                if (/^\d+\.\s/.test(line)) {
                                                    return <li key={i}>{line.replace(/^\d+\.\s/, '')}</li>;
                                                }
                                                // Regular paragraph
                                                return line ? <p key={i}>{line}</p> : <br key={i} />;
                                            })}
                                        </div>

                                        {/* External Links */}
                                        {section.externalLinks && section.externalLinks.length > 0 && (
                                            <div className="doc-external-links">
                                                {section.externalLinks.map((link, index) => (
                                                    <a
                                                        key={index}
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="doc-external-link"
                                                    >
                                                        <div className="doc-link-icon">📄</div>
                                                        <div className="doc-link-content">
                                                            <div className="doc-link-title">{link.title}</div>
                                                            {link.description && (
                                                                <div className="doc-link-description">{link.description}</div>
                                                            )}
                                                        </div>
                                                        <div className="doc-link-arrow">→</div>
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default Documentation;
